import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ThemeProvider,
  createTheme,
  Experimental_CssVarsProvider as CssVarsProvider,
  experimental_extendTheme as extendTheme,
  styled,
} from "@mui/material/styles";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import ThemesService from "../api/themes";
import { setTheme } from "../store/themeSlice";
import PropTypes from "prop-types";

const BorderLinearProgress = styled(LinearProgress)(() => ({
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#013C59",
  },
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: "#f37721",
  },
}));

export const ColorModeContext = React.createContext({
  toggleColorMode: () => { },
});

const initThemeLight = {
  primary: { main: "#F47721" },
  secondary: {
    main: "#161719",
  },
  custom: {
    boxShadowColor: "#BEBEBE",
    backgroundColor2: "#F1F1F1",
    text: "#1C2D41",
    textLightColor: "#1C2D41",
    textDarkColor: "#7C8798",
    borderColor: "#F47721",
    buttonColor: "#F47721",
    headerColor: "#F9FBFD",
    inputBackgroundColor: "#FFFFFF",
    inputTextColor: "#7C8798",
    buttonTextColor: "#E8EAEC",
    buttonHoverColor: "#E28131",
    buttonHoverTextColor: "#E8EAEC",
    backgroundColor: "#F9FBFD",
    navigationBackgroundColor: "#F9FBFD",
    hexagonBackgroundCOlor: "#2b2f38",
    navWidth: "200px",
  },
  typography: {
    fontFamily: `"Montserrat", "Rubik",  sans-serif`,
    fontheadingWeight: "bold",
  },
}
const initThemeDark = {
  primary: { main: "#F47721" },
  secondary: {
    main: "#161719",
  },
  custom: {
    boxShadowColor: "#2B2F38",
    backgroundColor2: "#0F0F0F",
    text: "#E8EAEC",
    textLightColor: "#E8EAEC",
    textDarkColor: "#7C8798",
    borderColor: "#F47721",
    buttonColor: "#F47721",
    headerColor: "#161719",
    inputBackgroundColor: "#2B2F38",
    inputTextColor: "#E8EAEC",
    buttonTextColor: "#E8EAEC",
    buttonHoverColor: "#BA5909",
    buttonHoverTextColor: "#E8EAEC",
    backgroundColor: "#161719",
    navigationBackgroundColor: "#161719",
    hexagonBackgroundCOlor: "#2b2f38",
    navWidth: "200px",
  },
  typography: {
    fontFamily: `"Montserrat", "Rubik",  sans-serif`,
    fontheadingWeight: "bold",
  },
}
export default function Theme({ children }) {
  const [mode, setMode] = useState(
    localStorage.getItem("color_theme") === "dark" ? "dark" : "light"
  );
  const organziation = useSelector(({ organziation }) => organziation);
  const [loading, setLoadingValue] = useState(true);
  const dispatch = useDispatch();
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: (currentThemeagaian) => {
        dispatch(setTheme(currentThemeagaian === "light" ? "dark" : "light"));
        localStorage.setItem(
          "color_theme",
          currentThemeagaian === "light" ? "dark" : "light"
        );
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [themeLight, setThemeLight] = useState(
    localStorage.getItem("themeLightPalette")
      ? JSON.parse(localStorage.getItem("themeLightPalette"))
      : initThemeLight
  );
  const [themeDark, setThemeDark] = useState(
    localStorage.getItem("themeDarkPalette")
      ? JSON.parse(localStorage.getItem("themeDarkPalette"))
      : initThemeDark
  );

  useEffect(() => {
    // (orgObj && orgObj?.uid !== organziation?.uid) ||
    // (orgObj &&
    //   !localStorage.getItem("themeLightPalette") &&
    //   !localStorage.getItem("themeDarkPalette"))
    if (organziation) {
      // console.log("inititTheme", orgObj, organziation);
      const apiKey = organziation?.api_key_list?.[0]?.key;
      const uid = organziation?.uid;
      ThemesService.getThemeProfileLink({
        uid: uid,
        apiKey,
      })
        .then((linkJson) => {
          for (
            let index = 0;
            index < linkJson.theme_profile_link.length;
            index++
          ) {
            ThemesService.getThemeProfile({
              theme_profile_id:
                linkJson.theme_profile_link[index].theme_profile_id,
              apiKey,
            })
              .then((profileJson) => {
                if (
                  !profileJson.theme_profile[0].name
                    .toLowerCase()
                    ?.includes("dark")
                ) {
                  ThemesService.GetThemeProfileAttributes({
                    theme_attribute_id: "",
                    theme_profile_id: profileJson.theme_profile[0].theme_id,
                    name: "",
                    status: "",
                    apiKey,
                  })
                    .then((attributeJson) => {
                      if (
                        attributeJson &&
                        attributeJson.theme_profile_attributes &&
                        attributeJson.theme_profile_attributes.length > 0
                      ) {
                        const primaryColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "primary-color"
                          ).value;
                        const textLightColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "text-light-color"
                          ).value;
                        const textDarkColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "text-dark-color"
                          ).value;
                        const headerColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "header-color"
                          ).value;
                        const inputBackgroundColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "input-background-color"
                          ).value;
                        const backgroundColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "background-color"
                          ).value;
                        const backgroundColor2 =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "background-color2"
                          ).value;
                        const navigationBackgroundColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "nav-color"
                          ).value;
                        const buttonColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-color"
                          ).value;
                        const buttonTextColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-text-color"
                          ).value;
                        const buttonHoverColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-hover-color"
                          ).value;
                        const buttonHoverTextColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-hover-text-color"
                          ).value;
                        const boxShadowColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "box-shadow-color"
                          ).value;
                        const navWidthValue =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "nav-width"
                          ).value;
                        const inputTextColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "input-text-color"
                          ).value;
                        const themeLightPalette = {
                          primary: {
                            main: backgroundColor ? backgroundColor : "#f47721",
                          },
                          secondary: {
                            main: backgroundColor ? backgroundColor : "#161719",
                          },
                          custom: {
                            boxShadowColor: boxShadowColor
                              ? boxShadowColor
                              : "#7c8798",
                            text: textLightColor ? textLightColor : "#fff",
                            textLightColor: textLightColor
                              ? textLightColor
                              : "#fff",
                            textDarkColor: textDarkColor
                              ? textDarkColor
                              : "#000",
                            borderColor: primaryColor
                              ? primaryColor
                              : "#e8c61b",
                            buttonColor: buttonColor ? buttonColor : "#161719",
                            headerColor: headerColor ? headerColor : "#161719",
                            inputBackgroundColor: inputBackgroundColor
                              ? inputBackgroundColor
                              : "#161719",
                            inputTextColor: inputTextColor
                              ? inputTextColor
                              : "#fff",
                            buttonTextColor: buttonTextColor
                              ? buttonTextColor
                              : "#161719",
                            buttonHoverColor: buttonHoverColor
                              ? buttonHoverColor
                              : "#161719",
                            buttonHoverTextColor: buttonHoverTextColor
                              ? buttonHoverTextColor
                              : "#161719",
                            backgroundColor: backgroundColor
                              ? backgroundColor
                              : "#161719",
                            backgroundColor2: backgroundColor2
                              ? backgroundColor2
                              : "#161719",
                            navigationBackgroundColor: navigationBackgroundColor
                              ? navigationBackgroundColor
                              : backgroundColor2,
                            hexagonBackgroundCOlor: boxShadowColor
                              ? boxShadowColor
                              : "#7C8798",

                            navWidth: navWidthValue ? navWidthValue : "200px",
                          },
                        };
                        setLoadingValue(false);
                        // const lightthemeca =
                        //   localStorage.getItem("themeLightPalette");
                        localStorage.setItem(
                          "themeLightPalette",
                          JSON.stringify(themeLightPalette)
                        );
                        setThemeLight(themeLightPalette);
                      }
                    })
                    .catch((errorr) => {
                      if (process.env.NODE_ENV === "development")
                        console.log("themes error", errorr);
                    });
                } else {
                  ThemesService.GetThemeProfileAttributes({
                    theme_attribute_id: "",
                    theme_profile_id: profileJson.theme_profile[0].theme_id,
                    name: "",
                    status: "",
                    apiKey,
                  })
                    .then((attributeJson) => {
                      if (
                        attributeJson &&
                        attributeJson.theme_profile_attributes &&
                        attributeJson.theme_profile_attributes.length > 0
                      ) {
                        const primaryColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "primary-color"
                          ).value;
                        const textLightColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "text-light-color"
                          ).value;
                        const textDarkColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "text-dark-color"
                          ).value;
                        const headerColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "header-color"
                          ).value;
                        const inputBackgroundColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "input-background-color"
                          ).value;
                        const backgroundColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "background-color"
                          ).value;
                        const backgroundColor2 =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "background-color2"
                          ).value;
                        const navigationBackgroundColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "nav-color"
                          ).value;
                        const buttonColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-color"
                          ).value;
                        const buttonTextColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-text-color"
                          ).value;
                        const buttonHoverColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-hover-color"
                          ).value;
                        const buttonHoverTextColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "button-hover-text-color"
                          ).value;
                        const boxShadowColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "box-shadow-color"
                          ).value;
                        const navWidthValue =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "nav-width"
                          ).value;
                        const inputTextColor =
                          attributeJson.theme_profile_attributes.find(
                            (x) => x.name === "input-text-color"
                          ).value;
                        const themeDarkPalette = {
                          primary: {
                            main: primaryColor ? primaryColor : "#f47721",
                          },
                          secondary: {
                            main: backgroundColor2
                              ? backgroundColor2
                              : "#161719",
                          },
                          custom: {
                            boxShadowColor: boxShadowColor
                              ? boxShadowColor
                              : "#7c8798",
                            text: textLightColor ? textLightColor : "#fff",
                            textLightColor: textLightColor
                              ? textLightColor
                              : "#fff",
                            textDarkColor: textDarkColor
                              ? textDarkColor
                              : "#000",
                            borderColor: primaryColor
                              ? primaryColor
                              : "#e8c61b",
                            buttonColor: buttonColor ? buttonColor : "#161719",
                            headerColor: headerColor ? headerColor : "#161719",
                            inputBackgroundColor: inputBackgroundColor
                              ? inputBackgroundColor
                              : "#161719",
                            inputTextColor: inputTextColor
                              ? inputTextColor
                              : "#fff",
                            buttonTextColor: buttonTextColor
                              ? buttonTextColor
                              : "#161719",
                            buttonHoverColor: buttonHoverColor
                              ? buttonHoverColor
                              : "#161719",
                            buttonHoverTextColor: buttonHoverTextColor
                              ? buttonHoverTextColor
                              : "#161719",
                            backgroundColor: backgroundColor
                              ? backgroundColor
                              : "#161719",
                            backgroundColor2: backgroundColor2
                              ? backgroundColor2
                              : "#161719",
                            navigationBackgroundColor: navigationBackgroundColor
                              ? navigationBackgroundColor
                              : backgroundColor2,
                            hexagonBackgroundCOlor: boxShadowColor
                              ? boxShadowColor
                              : "#7C8798",
                            navWidth: navWidthValue ? navWidthValue : "200px",
                          },
                        };
                        // const lightthemeca =
                        //   localStorage.getItem("themeDarkPalette");
                        setLoadingValue(false);
                        localStorage.setItem(
                          "themeDarkPalette",
                          JSON.stringify(themeDarkPalette)
                        );
                        setThemeDark(themeDarkPalette);
                      }
                    })
                    .catch((errorr) => {
                      if (process.env.NODE_ENV === "development")
                        console.log("themes error", errorr);
                    });
                }
              })
              .catch((errorr) => {
                if (process.env.NODE_ENV === "development")
                  console.log("themes error", errorr);
              });
          }
          setLoadingValue(false);
        })
        .catch((errorr) => {
          if (process.env.NODE_ENV === "development")
            console.log("themes error", errorr);
        });
    } else {
      setThemeLight(initThemeLight);
      setThemeDark(initThemeDark);
      setLoadingValue(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organziation]);
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "dark" ? themeDark : themeLight),
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
          },
        },
        typography: {
          fontFamily: `"Montserrat", "Rubik",  sans-serif`,
          fontheadingWeight: "bold",
          textSizeNormal: "0.9rem",
          button: {
            textTransform: "none",
          },
        },
      }),
    [mode, themeDark, themeLight]
  );
  const extendedThemeState = React.useMemo(
    () =>
      extendTheme({
        palette: {
          mode,
          ...(mode === "dark" ? themeDark : themeLight),
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
          },
        },
        typography: {
          fontFamily: `"Montserrat", "Rubik",  sans-serif`,
          fontheadingWeight: "bold",
          textSizeNormal: "0.9rem",
          button: {
            textTransform: "none",
          },
        },
      }),
    [mode, themeDark, themeLight]
  );
  return (
    <>
      {loading ? (
        <div
          style={{
            display: "flex",
            flex: "1 1 0%",
            flexFlow: "col",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "black",
            height: "98vh",
            margin: "-8px",
          }}
        >
          <BorderLinearProgress
            variant="indeterminate"
            sx={{ width: "192px" }}
            className="w-192 sm:w-320 max-w-full rounded-2"
          />
        </div>
      ) : (
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssVarsProvider theme={extendedThemeState}>
              {children}
            </CssVarsProvider>
          </ThemeProvider>
        </ColorModeContext.Provider>
      )}
    </>
  );
}
Theme.propTypes = {
  children: PropTypes.node,
};
